import React, { useEffect, useState } from "react";
import {
  Button,
  Modal,
  Tooltip,
  Tag,
  Switch,
  TimePicker,
} from "antd";
import moment from "moment";
import firebase from "../../Firebase.js";
import {
  EditOutlined,
  DeleteOutlined,
  CloudUploadOutlined,
  LinkOutlined,
  ClockCircleOutlined,
  ExclamationCircleFilled,
} from "@ant-design/icons";
import { Zap, Calendar } from "lucide-react";
import PopupManager from "../../managers/PopupManager";
import PermissionManager from "../../managers/PermissionManager";
import {
  useProLanguage,
  PRO_TRANSLATIONS,
} from "../../global/ProLanguageContext";
import Constants from "../../data/constants";
import MomentHelper from "../../helpers/MomentHelper";
import EventScheduler from "../EventScheduler";
import { motion, AnimatePresence } from "framer-motion";
import Animations from "../../animations/Animations";

var db;

const btnBase = {
  width: 30,
  height: 30,
  borderRadius: 8,
  border: "1px solid #e2e8f0",
  background: "#fff",
  color: "#64748b",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};
const btnDanger = { ...btnBase, border: "1px solid #fca5a5", color: "#dc2626" };
const btnAccent = { ...btnBase, border: "1px solid #c7d2fe", color: "#4f46e5" };

const EventPlaylistTableView = ({
  data,
  onRowClicked = () => {},
  onScreenClicked = () => {},
  onPlaylistClicked = () => {},
  onDelete = () => {},
  onEventScheduleUpdated = () => {},
  onEdit = () => {},
}) => {
  const { lang } = useProLanguage();
  const t = (PRO_TRANSLATIONS[lang] || PRO_TRANSLATIONS.en).eventPage;

  const [screensStatus, setScreensStatus] = useState({});

  const handleFirebase = () => {
    if (!data.length) return;
    const { AccountCode = "", BrandCode = "", BranchCode = "" } = data[0];
    if (db) {
      db.off("child_changed");
      db.off("child_added");
      db = null;
    }
    db = firebase
      .database()
      .ref()
      .child(`${AccountCode}/${BrandCode}/${BranchCode}`);
    db.on("child_changed", (snap) => {
      screensStatus[snap.key] = { ...screensStatus[snap.key], ...snap.val() };
      setScreensStatus({ ...screensStatus });
    });
    db.on("child_added", (snap) => {
      if (!screensStatus[snap.key]) screensStatus[snap.key] = {};
      screensStatus[snap.key] = { ...screensStatus[snap.key], ...snap.val() };
      setScreensStatus({ ...screensStatus });
    });
  };
  useEffect(() => {
    handleFirebase();
  }, []);
  useEffect(() => {
    handleFirebase();
  }, [data]);

  const launchAssignAndPublish = (eventPlaylistData) => {
    PopupManager.launchAssignAndPublishEventPopup(eventPlaylistData);
  };

  const editPermission = PermissionManager.checkPermission(
    PermissionManager.PERMISSION_KEY_SCREEN,
    PermissionManager.PERMISSION_TYPE_EDIT,
  );
  const deletePermission = PermissionManager.checkPermission(
    PermissionManager.PERMISSION_KEY_SCREEN,
    PermissionManager.PERMISSION_TYPE_DELETE,
  );
  const viewCampaignPermission = PermissionManager.checkPermission(
    PermissionManager.PERMISSION_KEY_CAMPAIGN,
    PermissionManager.PERMISSION_TYPE_LIST,
  );
  const allowPublishPermission = PermissionManager.checkPermission(
    PermissionManager.PERMISSION_KEY_EVENT,
    PermissionManager.PERMISSION_TYPE_PUBLISH,
  );

  if (!data.length) {
    return (
      <div style={{ textAlign: "center", padding: "60px 20px" }}>
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: 18,
            background: "linear-gradient(135deg,#fffbeb,#fef3c7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 16px",
          }}
        >
          <Calendar size={28} color="#f59e0b" />
        </div>
        <div
          style={{
            fontSize: 16,
            fontWeight: 600,
            color: "#0f172a",
            marginBottom: 6,
          }}
        >
          {t.noEventsTitle}
        </div>
        <div style={{ fontSize: 13, color: "#94a3b8" }}>{t.noEventsDesc}</div>
      </div>
    );
  }

  return (
    <motion.div
      initial="hidden"
      animate="show"
      variants={{ hidden: {}, show: { transition: { staggerChildren: 0.05 } } }}
    >
      {data.map((record, index) => {
        const {
          EventPlaylistName,
          EventTypeName,
          EventTypeColor,
          PlaylistName,
          Settings,
        } = record;

        let settingJson = {};
        try {
          settingJson = JSON.parse(Settings);
        } catch (_) {}
        const durationTime = MomentHelper.convertDurationToTimeFormat(
          settingJson.duration,
        );
        const delayAfter = MomentHelper.convertDurationToTimeFormat(
          settingJson.delayAfter,
        );

        const accentColor = EventTypeColor || "#6366f1";

        return (
          <motion.div
            key={record.EventPlaylistID || index}
            variants={Animations.LIST_ITEM}
            style={{
              background: "#fff",
              border: "1px solid #f1f5f9",
              borderRadius: 18,
              marginBottom: 12,
              overflow: "hidden",
              display: "flex",
              boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
              transition: "box-shadow 0.2s ease",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.boxShadow =
                "0 6px 20px rgba(99,102,241,0.10)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.04)")
            }
          >
            {/* Accent bar */}
            <div
              style={{
                width: 4,
                flexShrink: 0,
                alignSelf: "stretch",
                background: accentColor,
              }}
            />

            {/* Content */}
            <div style={{ flex: 1, minWidth: 0 }}>
              {/* Top row: name + type + playlist */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "14px 20px 10px",
                  flexWrap: "wrap",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 10,
                      background: `${accentColor}18`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Zap size={16} color={accentColor} />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 15,
                        fontWeight: 700,
                        color: "#0f172a",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {EventPlaylistName}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        marginTop: 3,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: "#94a3b8",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                        }}
                      >
                        {t.trigger}
                      </span>
                      <Tooltip title={t.triggerTooltip}>
                        <Tag
                          color={EventTypeColor}
                          style={{ margin: 0, fontSize: 11, fontWeight: 600 }}
                        >
                          {EventTypeName}
                        </Tag>
                      </Tooltip>
                    </div>
                  </div>
                </div>

                {/* Playlist chip */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    flexShrink: 0,
                  }}
                >
                  <Tooltip title={t.playlistTooltip}>
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#94a3b8",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      {t.playlist}
                    </span>
                  </Tooltip>
                  {PlaylistName ? (
                    <button
                      onClick={() => onPlaylistClicked(record)}
                      disabled={!viewCampaignPermission}
                      style={{
                        padding: "4px 10px",
                        borderRadius: 20,
                        border: "1.5px dashed #c7d2fe",
                        background: "#eef2ff",
                        color: "#4f46e5",
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: viewCampaignPermission
                          ? "pointer"
                          : "not-allowed",
                      }}
                    >
                      <LinkOutlined style={{ marginRight: 4, fontSize: 11 }} />
                      {PlaylistName}
                    </button>
                  ) : (
                    <button
                      onClick={() => onPlaylistClicked(record)}
                      disabled={!editPermission}
                      style={{
                        padding: "4px 10px",
                        borderRadius: 20,
                        border: "1.5px dashed #e2e8f0",
                        background: "#f8fafc",
                        color: "#94a3b8",
                        fontSize: 12,
                        fontWeight: 500,
                        cursor: editPermission ? "pointer" : "not-allowed",
                      }}
                    >
                      {t.selectPlaylist}
                    </button>
                  )}
                </div>
              </div>

              {/* Bottom row: settings + schedule + actions */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  padding: "8px 20px 14px",
                  borderTop: "1px solid #f8fafc",
                  flexWrap: "wrap",
                }}
              >
                {/* Settings chips */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: "#94a3b8",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    {t.settings}
                  </span>
                  <Tooltip
                    title={
                      settingJson.allowInterrupt
                        ? t.interruptibleTooltip
                        : t.nonInterruptibleTooltip
                    }
                  >
                    <span
                      style={{
                        fontSize: 11,
                        padding: "3px 8px",
                        borderRadius: 20,
                        background: settingJson.allowInterrupt
                          ? "#f0fdf4"
                          : "#fff5f5",
                        color: settingJson.allowInterrupt
                          ? "#16a34a"
                          : "#dc2626",
                        border: `1px solid ${settingJson.allowInterrupt ? "#bbf7d0" : "#fca5a5"}`,
                        fontWeight: 600,
                      }}
                    >
                      {settingJson.allowInterrupt
                        ? t.interruptible
                        : t.nonInterruptible}
                    </span>
                  </Tooltip>
                  {durationTime && (
                    <Tooltip title={t.durationTooltip}>
                      <span
                        style={{
                          fontSize: 11,
                          padding: "3px 8px",
                          borderRadius: 20,
                          background: "#f8fafc",
                          color: "#475569",
                          border: "1px solid #e2e8f0",
                          fontWeight: 500,
                        }}
                      >
                        ⏱ {t.duration}: {durationTime}
                      </span>
                    </Tooltip>
                  )}
                  {delayAfter && (
                    <Tooltip title={t.cooldownTooltip}>
                      <span
                        style={{
                          fontSize: 11,
                          padding: "3px 8px",
                          borderRadius: 20,
                          background: "#f8fafc",
                          color: "#475569",
                          border: "1px solid #e2e8f0",
                          fontWeight: 500,
                        }}
                      >
                        ↩ {t.cooldown}: {delayAfter}
                      </span>
                    </Tooltip>
                  )}
                </div>


                {/* Spacer */}
                <div style={{ flex: 1 }} />

                {/* Schedule */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    flexShrink: 0,
                  }}
                >
                  <Tooltip title={t.scheduleTooltip}>
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#94a3b8",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      {t.schedule}
                    </span>
                  </Tooltip>
                  <EventScheduler
                    onSaved={onEventScheduleUpdated}
                    data={record}
                  />
                </div>

                {/* Actions */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    flexShrink: 0,
                  }}
                >
                  {editPermission && (
                    <Tooltip title={t.edit}>
                      <button style={btnBase} onClick={() => onEdit(record)}>
                        <EditOutlined style={{ fontSize: 13 }} />
                      </button>
                    </Tooltip>
                  )}
                  {allowPublishPermission && (
                    <Tooltip title={t.assignToScreens}>
                      <button
                        style={btnAccent}
                        onClick={() => launchAssignAndPublish(record)}
                      >
                        <CloudUploadOutlined style={{ fontSize: 13 }} />
                      </button>
                    </Tooltip>
                  )}
                  {deletePermission && (
                    <Tooltip title={t.delete}>
                      <button
                        style={btnDanger}
                        onClick={() => {
                          Modal.confirm({
                            title: t.deleteConfirm,
                            icon: <ExclamationCircleFilled />,
                            okText: t.delete,
                            cancelText: t.cancel,
                            centered: true,
                            onOk: () => onDelete(record),
                          });
                        }}
                      >
                        <DeleteOutlined style={{ fontSize: 13 }} />
                      </button>
                    </Tooltip>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        );
      })}
    </motion.div>
  );
};

export default EventPlaylistTableView;
